import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import { createReportURL } from 'core/routes';
import { useRepairStatusReportCovea, convertToDate } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Workbook } from 'exceljs';
import {
  dateToString,
  formatDate,
  formatDateTime,
} from '../../../utils/helpers';
import { AxiosError } from 'axios';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import {
  COVEA_REPORT_GRID_TITLE,
  COVEA_REPORT_MESSAGE,
  COVEA_REPORT_TITLE,
  columns,
} from './constants';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { ReportSearchForm } from 'components/ReportForms/ReportSearchForm';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import { useReportParams } from 'pages/ReportsDetails/hooks';
import { ReportProps } from 'pages/ReportsDetails/types';
import DevExtremeReportGrid from 'components/ReportGrid_New';
import { createRepairStatusUsageSummarySheet } from './helpers';
import { NotificationSummary } from './NotificationSummary';
import { shortDateFormat } from 'components/ReportGrid_New/constants';


export function CoveaReport({
  reportPath,
  setErrorMessage,
  setToast,
}: Readonly<ReportProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useReportParams(location.search);

  const [startDate, setStartDate] = useState<Date | null>(
    convertToDate(urlParams.startDate) ?? null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    convertToDate(urlParams.endDate) ?? null
  );

  const queryEnabled = !!urlParams.startDate && !!urlParams.endDate;
  const { reportData, refetchReport, isReportFetching } =
    useRepairStatusReportCovea(
      {
        startDate: urlParams.startDate,
        endDate: urlParams.endDate,
      },
      queryEnabled,
      handleError
    );

  const rows = useMemo(
    () => reportData?.repairSatus ?? undefined,
    [reportData?.repairSatus]
  );

  const chartData = useMemo(
    () => [
      {
        state: 'Summary',
        totalNotifications: reportData?.mobileCount?.recs,
        totalRepairs: reportData?.mobileCount?.repairCount,
        totalMobileNumberSupplied: reportData?.mobileCount?.mobileNumbers,
      },
    ],
    [reportData]
  );

  useEffect(() => {
    navigateToReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.startDate, urlParams.endDate]);

  const reportHeaderTagLine = useMemo(
    () =>
      `Report produced on ${formatDateTime(new Date())} from ${formatDate(startDate)} to ${formatDate(endDate)}`,
    [startDate, endDate]
  );

  return (
    <MainPageContainer isLoading={isReportFetching} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={COVEA_REPORT_TITLE}
          infoMessage={COVEA_REPORT_MESSAGE}
        />
        <ReportSearchForm
          endDate={endDate}
          isDisabled={isReportFetching}
          startDate={startDate}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          handleSubmit={handleSubmit}
        />
      </UpperInfoContainer>

      {!isReportFetching && rows && rows?.length === 0 && <NoReportDataMessage />}
      {!isReportFetching && rows && rows?.length > 0 && (
        <NotificationSummary chartData={chartData} reportData={reportData} />
      )}

      <ReportGridContainer direction="column">
        <DevExtremeReportGrid
          columns={columns}
          dateFormat={shortDateFormat}
          displayGroupingPanel
          headerTitleForSheet={COVEA_REPORT_TITLE}
          isLoading={isReportFetching}
          reportHeaderTagLine={reportHeaderTagLine}
          rows={rows}
          title={COVEA_REPORT_GRID_TITLE}
          handleNewSheet={handleNewSheet}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  async function handleNewSheet(
    workbook: Workbook,
    headerImageId: number
  ) {
    try {
      await createRepairStatusUsageSummarySheet(workbook, headerImageId, reportHeaderTagLine, COVEA_REPORT_TITLE, reportData);
    } catch (error: any) {
      if (
        error?.message?.includes('Worksheet name already exists')
      ) {
        return;
      }
      reportLogger(error);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      urlParams.startDate == dateToString(startDate) &&
      urlParams.endDate == dateToString(endDate)
    )
      refetchReport();
    else navigateToReport();
  }

  function navigateToReport() {
    if (!!startDate || !!endDate)
      navigate(
        createReportURL(
          {
            startDate: dateToString(startDate),
            endDate: dateToString(endDate),
          },
          reportPath,
        ), { replace: true });
  }
  function handleError(error: AxiosError) {
    reportLogger(error);
    setErrorMessage(errorMessages.reports(COVEA_REPORT_TITLE));
    setToast(true);
  }
}
